import * as React from 'react';
import flattenChildren from 'react-keyed-flatten-children';
import { NavigationMenu } from './NavigationMenu.js';
import { Id } from '../../utils/ids.js';
import { Bem, addClass } from '../../utils/css.js';
import { isElement } from '../../utils/component-util.js';
import { createSizeObserver } from '../../utils/common.js';
import { NavigationToggle } from './NavigationToggle.js';
import { NavigationLogo } from './NavigationLogo.js';
import { NavigationProductName } from './NavigationProductName.js';
import { NavigationSubMenu } from './NavigationSubMenu.js';
import { NavigationItem } from './NavigationItem.js';
import { NavigationScrollbar } from './NavigationScrollbar.js';
import './Navigation.scss.js';
import { NavigationContext } from './NavigationContext.js';
import { Key } from '../../constants.js';

const placeHolderScrollDimensions = {
  scrollTop: 0,
  clientHeight: 0,
  scrollHeight: 0
};
const bem = Bem.create("vai-navigation");
const calculateScroll = (deltaY, menu) => {
  const { scrollTop, scrollHeight, clientHeight } = menu;
  if (scrollTop + deltaY + clientHeight > scrollHeight) {
    return scrollHeight - clientHeight;
  } else {
    return Math.max(scrollTop + deltaY, 0);
  }
};
const Navigation = (props) => {
  const {
    animated = true,
    className,
    header,
    children,
    footer,
    productName,
    dataTa = "vai-navigation",
    collapsed,
    htmlId,
    _collapseMode = "stripe",
    selectedKey,
    openSubmenuKeys,
    onSelectItem,
    onSubmenuToggle,
    onCollapseToggle
  } = props;
  const id = Id.create(htmlId, ["navigation menu"]);
  const [{ scrollTop, scrollHeight, clientHeight }, setScroll] = React.useState(placeHolderScrollDimensions);
  const menuRef = React.useRef(null);
  React.useEffect(() => {
    if (scrollTop > scrollHeight - clientHeight) {
      menuRef.current.scrollTop = scrollHeight - clientHeight;
    } else {
      menuRef.current.scrollTop = scrollTop;
    }
  }, [menuRef, clientHeight, scrollTop, scrollHeight]);
  const getMenu = React.useCallback(() => ({
    scrollTop: menuRef.current?.scrollTop ?? 0,
    scrollHeight: menuRef.current?.scrollHeight ?? 0,
    clientHeight: menuRef.current?.clientHeight ?? 0
  }), [menuRef]);
  const updateScroll = React.useCallback(() => {
    if (menuRef.current === null) {
      return;
    }
    setScroll(getMenu());
  }, [menuRef, setScroll, getMenu]);
  const queueUpdateScroll = React.useCallback(() => {
    requestAnimationFrame(updateScroll);
  }, [updateScroll]);
  React.useEffect(queueUpdateScroll, [queueUpdateScroll]);
  React.useEffect(() => {
    if (!menuRef.current) {
      return;
    }
    createSizeObserver({
      debounce: 10,
      element: menuRef.current,
      onChange: queueUpdateScroll
    });
  }, [menuRef, queueUpdateScroll]);
  const onWheel = React.useCallback((e) => {
    const { deltaY } = e;
    setScroll((state) => ({ ...state, scrollTop: calculateScroll(deltaY, getMenu()) }));
  }, [getMenu]);
  const [touchY, setTouchY] = React.useState(void 0);
  const onTouch = React.useCallback((e) => {
    const { clientY } = e.targetTouches[0];
    if (touchY === void 0) {
      setTouchY(clientY);
      return;
    }
    setScroll((state) => ({ ...state, scrollTop: calculateScroll(touchY - clientY, getMenu()) }));
    setTouchY(clientY);
  }, [setScroll, setTouchY, getMenu, touchY]);
  const onTouchEnd = React.useCallback(() => setTouchY(void 0), [setTouchY]);
  const handleClick = React.useCallback((e) => {
    if (e.target instanceof HTMLDivElement && e.target.classList.contains(bem.block()) && e.clientX > e.target.offsetWidth) {
      onCollapseToggle?.();
    }
  }, [onCollapseToggle]);
  const contextValue = React.useMemo(() => ({
    selectedKey,
    openSubmenuKeys,
    onSelectItem,
    onSubmenuToggle,
    floatingSubMenu: _collapseMode === "bar",
    collapsed
  }), [_collapseMode, collapsed, onSelectItem, onSubmenuToggle, openSubmenuKeys, selectedKey]);
  const navigableItemRefs = React.useRef(new Set());
  const addItemRefs = React.useCallback((el) => {
    if (!el || navigableItemRefs.current.has(el)) {
      return;
    }
    const role = el.getAttribute("role");
    const link = el.querySelector("a");
    if (role === "menu") {
      navigableItemRefs.current.add(el);
    }
    if (role === "menuitem" && link) {
      navigableItemRefs.current.add(link);
    }
  }, [navigableItemRefs]);
  const hookedChildren = React.useMemo(() => flattenChildren(children).map((child) => {
    if (!React.isValidElement(child)) {
      return child;
    }
    if (isElement(child, NavigationSubMenu) || isElement(child, NavigationItem)) {
      return React.cloneElement(child, {
        _internalRef: addItemRefs
      });
    }
    return child;
  }), [children, addItemRefs]);
  const onKeyDown = React.useCallback((event) => {
    if (![Key.ArrowUp, Key.ArrowDown, Key.Home, Key.End].includes(event.key)) {
      return;
    }
    event.stopPropagation();
    const navigableItems = Array.from(navigableItemRefs.current);
    if (navigableItems.length > 0 && !navigableItems.some((el) => el === document.activeElement)) {
      navigableItems[0].focus();
      return;
    }
    const currentIndex = navigableItems.findIndex((el) => el === document.activeElement);
    const firstIndex = 0;
    const lastIndex = navigableItems.length - 1;
    switch (event.key) {
      case Key.ArrowUp:
        if (currentIndex > firstIndex) {
          navigableItems[currentIndex - 1].focus();
        }
        break;
      case Key.ArrowDown:
        if (currentIndex < lastIndex) {
          navigableItems[currentIndex + 1].focus();
        }
        break;
      case Key.Home:
        navigableItems[firstIndex].focus();
        break;
      case Key.End:
        navigableItems[lastIndex].focus();
        break;
    }
  }, [navigableItemRefs]);
  const open = !collapsed;
  return /* @__PURE__ */ React.createElement(NavigationContext.Provider, {
    value: contextValue
  }, /* @__PURE__ */ React.createElement("div", {
    className: bem.element("wrapper")()
  }, /* @__PURE__ */ React.createElement("div", {
    className: addClass(bem.element("skeleton")(open ? "open" : "closed", animated && "animated"), "vai-navigation-context"),
    "data-collapse-mode": _collapseMode
  }), /* @__PURE__ */ React.createElement("div", {
    className: addClass(bem.block(open ? "open" : "closed", animated && "animated"), "vai-navigation-context", className),
    "data-collapse-mode": _collapseMode,
    onClick: handleClick
  }, /* @__PURE__ */ React.createElement(NavigationToggle, {
    open,
    onChange: onCollapseToggle,
    dataTa: `${dataTa}-toggle`,
    htmlId: "navigation-menu-toggle"
  }), /* @__PURE__ */ React.createElement("div", {
    id: id.copyWith(["header"]).toString(),
    "data-ta": `${dataTa}-header`,
    className: bem.element("header")()
  }, header ?? /* @__PURE__ */ React.createElement(NavigationLogo, {
    open
  })), /* @__PURE__ */ React.createElement("div", {
    id: id.toString(),
    ref: menuRef,
    "data-ta": dataTa,
    className: bem.element("content")(open ? "open" : "closed"),
    tabIndex: -1,
    onClick: queueUpdateScroll,
    onWheel,
    onTouchMove: onTouch,
    onTouchEnd
  }, /* @__PURE__ */ React.createElement(NavigationMenu, {
    onKeyDown
  }, hookedChildren, /* @__PURE__ */ React.createElement(NavigationScrollbar, {
    scrollTop,
    scrollHeight,
    clientHeight
  }))), /* @__PURE__ */ React.createElement("div", {
    id: id.copyWith(["footer"]).toString(),
    "data-ta": `${dataTa}-footer`,
    className: bem.element("footer")()
  }, footer ?? /* @__PURE__ */ React.createElement(NavigationProductName, {
    text: productName
  })))));
};

export { Navigation, calculateScroll, placeHolderScrollDimensions };
